import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { mockSave } from '../services/botConfigService.js';

const SENSITIVITY_OPTIONS = ['Low', 'Medium', 'High'];

export function HandoffPanel({ bot, updateBot, mockErrorOn, notify }) {
  const [enabled, setEnabled] = useState(bot.handoff.enabled);
  const [triggers, setTriggers] = useState(bot.handoff.triggers); // { explicitRequest, lowConfidence, keywordMatch }
  const [sensitivity, setSensitivity] = useState(bot.handoff.sensitivity);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keywords, setKeywords] = useState(bot.handoff.keywords);
  const [message, setMessage] = useState(bot.handoff.message);
  const [offHoursMode, setOffHoursMode] = useState(bot.handoff.offHoursMode); // 'queue' | 'offline'
  const [offlineMessage, setOfflineMessage] = useState(bot.handoff.offlineMessage);

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [confirmOff, setConfirmOff] = useState(false);

  const anyTriggerOn = triggers.explicitRequest || triggers.lowConfidence || triggers.keywordMatch;
  const isEmpty = !anyTriggerOn && !message;

  function toggleTrigger(key) {
    setTriggers((t) => ({ ...t, [key]: !t[key] }));
  }

  function addKeyword() {
    const val = keywordDraft.trim();
    if (!val || keywords.includes(val)) {
      setKeywordDraft('');
      return;
    }
    setKeywords((k) => [...k, val]);
    setKeywordDraft('');
  }

  function removeKeyword(k) {
    setKeywords((list) => list.filter((x) => x !== k));
  }

  function onToggleEnabled() {
    if (enabled) setConfirmOff(true);
    else setEnabled(true);
  }

  function validate() {
    const e = {};
    if (enabled) {
      if (!anyTriggerOn) e.triggers = 'Choose at least one escalation trigger.';
      if (triggers.lowConfidence && !sensitivity) e.sensitivity = 'Choose a confidence sensitivity.';
      if (triggers.keywordMatch && keywords.length === 0) e.keywords = 'Add at least one keyword.';
      if (!message || message.trim().length < 10) e.message = 'Handoff message must be at least 10 characters.';
      else if (message.length > 300) e.message = 'Handoff message must be under 300 characters.';
      if (offHoursMode === 'offline' && (!offlineMessage || offlineMessage.trim().length < 10)) {
        e.offlineMessage = 'Offline message must be at least 10 characters.';
      }
    }
    return e;
  }

  function save() {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setStatus('saving');
    mockSave(mockErrorOn)
      .then(() => {
        updateBot({
          handoff: { enabled, triggers, sensitivity, keywords, message, offHoursMode, offlineMessage },
        });
        setStatus('idle');
        notify('success', 'Handoff settings saved');
      })
      .catch(() => setStatus('error'));
  }

  return (
    <div>
      <div className="panel-header">
        <h1>Human-handoff behavior</h1>
        <p>Decide when your bot should bring in a human, and what visitors see while they wait.</p>
      </div>
      {status === 'error' && <AlertBanner text="We couldn't save your changes. Try again." onRetry={save} />}

      <div className="card">
        <div className="field">
          <div className="toggle-row">
            <div className="toggle-copy">
              <strong>Enable human handoff</strong>
              <span>Turns escalation to a human agent on or off for this bot.</span>
            </div>
            <button className={'switch' + (enabled ? ' on' : '')} onClick={onToggleEnabled} aria-pressed={enabled}>
              <span className="knob"></span>
            </button>
          </div>
        </div>

        {!enabled ? (
          <div className="empty-state" style={{ paddingTop: 10 }}>
            <div className="empty-icon"><Icon.msg s={22} /></div>
            <h3>Human handoff is turned off</h3>
            <p>Your bot will never offer to connect visitors with a person while this is off.</p>
          </div>
        ) : isEmpty ? (
          <div className="empty-state" style={{ paddingTop: 10 }}>
            <div className="empty-icon"><Icon.msg s={22} /></div>
            <h3>Decide when your bot should bring in a human</h3>
            <p>Pick at least one trigger below and write the message visitors see when they're handed off.</p>
          </div>
        ) : null}

        {enabled && (
          <>
            <div className={'field' + (errors.triggers ? ' invalid' : '')}>
              <label>Escalation triggers</label>
              <div className="lang-option-row">
                <button
                  type="button"
                  className={'lang-pill' + (triggers.explicitRequest ? ' added' : '')}
                  onClick={() => toggleTrigger('explicitRequest')}
                >
                  {triggers.explicitRequest ? '✓ ' : '+ '}Visitor asks for a human
                </button>
                <button
                  type="button"
                  className={'lang-pill' + (triggers.lowConfidence ? ' added' : '')}
                  onClick={() => toggleTrigger('lowConfidence')}
                >
                  {triggers.lowConfidence ? '✓ ' : '+ '}Bot confidence is low
                </button>
                <button
                  type="button"
                  className={'lang-pill' + (triggers.keywordMatch ? ' added' : '')}
                  onClick={() => toggleTrigger('keywordMatch')}
                >
                  {triggers.keywordMatch ? '✓ ' : '+ '}Specific keywords used
                </button>
              </div>
              {errors.triggers && <div className="error-text"><Icon.alert s={12} /> {errors.triggers}</div>}
            </div>

            {triggers.lowConfidence && (
              <div className={'field' + (errors.sensitivity ? ' invalid' : '')}>
                <label htmlFor="sensitivity">Confidence sensitivity</label>
                <select id="sensitivity" value={sensitivity} onChange={(e) => setSensitivity(e.target.value)}>
                  <option value="">Select sensitivity…</option>
                  {SENSITIVITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.sensitivity && <div className="error-text"><Icon.alert s={12} /> {errors.sensitivity}</div>}
                <div className="hint">Higher sensitivity escalates sooner, even when the bot is only slightly unsure.</div>
              </div>
            )}

            {triggers.keywordMatch && (
              <div className={'field' + (errors.keywords ? ' invalid' : '')}>
                <label htmlFor="keyword-input">Escalation keywords</label>
                <input
                  id="keyword-input"
                  type="text"
                  placeholder="Type a keyword and press Enter (e.g. refund, cancel, agent)"
                  value={keywordDraft}
                  onChange={(e) => setKeywordDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addKeyword();
                    }
                  }}
                />
                {errors.keywords && <div className="error-text"><Icon.alert s={12} /> {errors.keywords}</div>}
                {keywords.length > 0 && (
                  <div className="chip-row">
                    {keywords.map((k) => (
                      <span className="chip" key={k}>{k}<button onClick={() => removeKeyword(k)}>×</button></span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className={'field' + (errors.message ? ' invalid' : '')}>
              <label htmlFor="handoff-message">
                Handoff message
                <span className={'counter' + (message.length > 300 ? ' danger' : '')}>{message.length} / 300</span>
              </label>
              <textarea
                id="handoff-message"
                rows={3}
                placeholder="e.g. Let me connect you with a member of our team — one moment please."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              {errors.message && <div className="error-text"><Icon.alert s={12} /> {errors.message}</div>}
            </div>

            {message && (
              <div className="field">
                <div className="preview-label">Preview</div>
                <div className="bubble">{message}</div>
              </div>
            )}

            <div className="field">
              <label>Outside business hours</label>
              <div className="lang-option-row">
                <button
                  type="button"
                  className={'lang-pill' + (offHoursMode === 'queue' ? ' added' : '')}
                  onClick={() => setOffHoursMode('queue')}
                >
                  {offHoursMode === 'queue' ? '✓ ' : ''}Queue for next available agent
                </button>
                <button
                  type="button"
                  className={'lang-pill' + (offHoursMode === 'offline' ? ' added' : '')}
                  onClick={() => setOffHoursMode('offline')}
                >
                  {offHoursMode === 'offline' ? '✓ ' : ''}Show offline message instead
                </button>
              </div>
            </div>

            {offHoursMode === 'offline' && (
              <div className={'field' + (errors.offlineMessage ? ' invalid' : '')}>
                <label htmlFor="offline-message">Offline message</label>
                <textarea
                  id="offline-message"
                  rows={2}
                  placeholder="e.g. Our team is offline right now — we'll reply as soon as we're back."
                  value={offlineMessage}
                  onChange={(e) => setOfflineMessage(e.target.value)}
                />
                {errors.offlineMessage && <div className="error-text"><Icon.alert s={12} /> {errors.offlineMessage}</div>}
              </div>
            )}
          </>
        )}

        <div className="actions-row">
          <button className="btn-primary" disabled={status === 'saving'} onClick={save}>
            {status === 'saving' && <span className="spinner"></span>}
            {status === 'saving' ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {confirmOff && (
        <ConfirmDialog
          title="Turn off human handoff?"
          body="Visitors won't be able to reach a human when the bot can't help. Continue?"
          confirmLabel="Turn off"
          onConfirm={() => {
            setEnabled(false);
            setConfirmOff(false);
          }}
          onCancel={() => setConfirmOff(false)}
        />
      )}
    </div>
  );
}
